import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { to: '/', label: 'Accueil' },
  { to: '/formations', label: 'Formations' },
  { to: '/a-propos', label: 'À propos' },
  { to: '/actualites', label: 'Actualités' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)] transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-200/60' : 'bg-white/70 backdrop-blur-md'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="flex items-center justify-between min-h-[4.25rem] py-3 sm:py-4">
          <Link to="/" className="flex min-h-11 items-center gap-2 rounded-lg py-1 pr-2 -ml-2 pl-2 group touch-manipulation active:bg-slate-100/80">
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/30 group-hover:scale-105 transition-transform">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-bold text-slate-900 tracking-tight">Medelia</span>
              <span className="text-[10px] text-teal-600 font-semibold tracking-widest uppercase">Santé & Formation</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `inline-flex min-h-10 items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors ${isActive ? 'text-teal-700 bg-teal-50' : 'text-slate-700 hover:text-teal-700 hover:bg-slate-50'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20" asChild>
              <Link to="/inscription">S'inscrire</Link>
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="lg:hidden inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-slate-700 touch-manipulation hover:bg-slate-100"
            aria-expanded={open}
            aria-controls="mobile-nav-menu"
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {open ? <X className="w-6 h-6" aria-hidden /> : <Menu className="w-6 h-6" aria-hidden />}
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-nav-menu"
          className="lg:hidden max-h-[min(70vh,calc(100dvh-5rem))] overflow-y-auto overscroll-contain border-t border-slate-200 bg-white px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-inner"
          role="navigation"
          aria-label="Navigation principale"
        >
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block min-h-11 px-4 py-3 rounded-lg text-base font-medium touch-manipulation ${isActive ? 'text-teal-700 bg-teal-50' : 'text-slate-700 active:bg-slate-50'}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Button className="mt-3 h-12 w-full bg-teal-600 text-base hover:bg-teal-700 text-white touch-manipulation" asChild>
            <Link to="/inscription" onClick={() => setOpen(false)}>
              S'inscrire
            </Link>
          </Button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
